import type React from 'react';
import type { RefObject } from 'react';
import { useCallback, useState, useLayoutEffect, useRef } from 'react';
import { useIsListBoxOpen } from './useIsListBoxOpen';

export const useListBoxControls = ({
  optionsCount,
  selectedIndex,
  buttonRef: containerRef,
  selectIndex,
  selectValue,
}: {
  optionsCount: number;
  selectedIndex: number;
  buttonRef: RefObject<HTMLDivElement>;
  selectIndex: (index: number) => void;
  selectValue: (value: string) => number;
}): {
  isListBoxOpen: boolean;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  onButtonClick: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSelect: (value: string) => void;
  onBlur: (e: React.FocusEvent<HTMLDivElement>) => void;
  onOptionMouseDown: () => void;
} => {
  const { isListBoxOpen, toggle, open, close } = useIsListBoxOpen();
  const [activeIndex, setActiveIndex] = useState<number>(selectedIndex);
  const ignoreBlur = useRef<boolean>(false);

  useLayoutEffect(() => {
    // ensure activeindex doesn't go out of range due to options changing
    if (optionsCount >= activeIndex) return;
    setActiveIndex(optionsCount - 1);
  }, [activeIndex, optionsCount]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.code === 'ArrowDown') {
        e.preventDefault();
        if (!isListBoxOpen) {
          open();
          return;
        }
        setActiveIndex((index) => {
          if (index + 1 === optionsCount) return index;
          return index + 1;
        });
        return;
      }
      if (e.code === 'ArrowUp') {
        e.preventDefault();
        if (!isListBoxOpen) {
          open();
          return;
        }
        setActiveIndex((index) => {
          if (index === 0) return index;
          return index - 1;
        });
      }
      if (e.code === 'Space' || e.code === 'Enter' || e.code === 'Tab') {
        if (e.code !== 'Tab') {
          e.preventDefault();
        }
        toggle();
        selectIndex(activeIndex);
        return;
      }
      if (e.code === 'Escape') {
        e.preventDefault();
        close();
      }
    },
    [isListBoxOpen, open, close, optionsCount, toggle, selectIndex, activeIndex],
  );

  const onSelect = useCallback(
    (value: string) => {
      const index = selectValue(value);
      setActiveIndex(index);
      toggle();
      containerRef.current?.focus();
    },
    [toggle, selectValue, containerRef],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (ignoreBlur.current === true) {
        e.preventDefault();
        ignoreBlur.current = false;
        return;
      }
      close();
    },
    [ignoreBlur, close],
  );

  const onOptionMouseDown = useCallback(() => {
    ignoreBlur.current = true;
  }, []);

  const onButtonClick = useCallback(() => {
    toggle();
  }, [toggle]);

  return {
    isListBoxOpen,
    activeIndex: Math.min(activeIndex, optionsCount - 1),
    setActiveIndex,
    onKeyDown,
    onSelect,
    onBlur,
    onOptionMouseDown,
    onButtonClick,
  };
};
